import React, { memo, useCallback, useMemo, useState } from 'react';

import {
	Confirm,
	Modal,
	PageTitle,
	SpinnerOverlay,
	Table,
	UnavailableFeatureMessage,
} from '@components/common';

import { Icons } from '@icons/icons';

import { useMutateSchemaClear } from '@hooks/configurationDataModelManagement/useMutateSchemaClear';
import { useMutateSchemaDelete } from '@hooks/configurationDataModelManagement/useMutateSchemaDelete';
import { useMutateSchemaDeleteOran } from '@hooks/configurationDataModelManagement/useMutateSchemaDeleteOran';
import { useMutateSchemaExport } from '@hooks/configurationDataModelManagement/useMutateSchemaExport';
import { useMutateSchemaRebuild } from '@hooks/configurationDataModelManagement/useMutateSchemaRebuild';
import useQuerySchemaConfig from '@hooks/configurationDataModelManagement/useQuerySchemaConfig';
import { useVendorDrivenFeatureAvailability } from '@hooks/utils/useVendorDrivenFeatureAvailability';

import { auth, configurationManagement, schemaConfig as schemaConfigSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setPage,
	setPageSize,
	setSort,
} from '@slices/schemaConfig';

import { IRebuildSchema, ISchemaConfigEntity } from '@interfaces/cmModule/schemaConfig';
import { IGroupOperationWithActionDTO, TFoldersId } from '@interfaces/common/defaultDTO';
import {
	ISortBy,
	ITablePagination,
	ITableSort,
} from '@interfaces/components/table';

import {
	EGroupMenuTitles,
	EMenuActionsTypes,
	EVendorsValues,
	TMenuActionsTypes,
} from '@enums/common';

import { MODAL_WIDTH_580 } from '@constants/modals';
import { menuActionsSchema } from './constants';

import AttachSchemaModel from './AttachSchemaModel';
import AttachYangFiles from './AttachYangFiles';
import {
	getColumns,
} from './manageConfigurationDataModelManagement';
import SchemaTree from './SchemaTree';
import ValidationRules from './ValidationRules';

import styles from './ConfigurationDataModelManagement.module.scss';

const ConfigurationDataModelManagement = () => {
	const dispatch = useAppDispatch();
	const authState = useAppSelector(auth);
	const { selectedVendor } = useAppSelector(configurationManagement);

	const {
		accessPermission:
			{
				networkOperatorCmModuleEditor,
				networkOperatorCmModuleAdmin,
			},
	} = authState;

	const isFeatureAvailable = useVendorDrivenFeatureAvailability();

	const isEditAccess = networkOperatorCmModuleAdmin || networkOperatorCmModuleEditor;

	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [checkedFolderIds, setCheckedFolderIds] = useState<Array<TFoldersId>>([]);
	const [selectedSchema, setSelectedSchema] = useState<ISchemaConfigEntity | null>(null);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();
	const [importYangName, setImportYangName] = useState<string>('');
	const [clearFolderId, setClearFolderId] = useState<string | undefined>();
	const [rebuildFolderData, setRebuildFolderData] = useState<IRebuildSchema | undefined>();

	const [isConfirmYangModalNeeded, setIsConfirmYangModalNeeded] = useState(false);
	const [isImportFormOpen, setIsImportFormOpen] = useState(false);
	const [isViewSchema, setIsViewSchema] = useState(false);
	const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
	const [isImportYangFilesModalOpen, setIsImportYangFilesModalOpen] = useState(false);
	const [isClearFolderConfirmOpen, setIsClearFolderConfirmOpen] = useState(false);
	const [isRebuildConfirmOpen, setIsRebuildConfirmOpen] = useState(false);

	const isOranVendor = useMemo(() => selectedVendor?.value === EVendorsValues.oran, [selectedVendor]);

	const openSchemaModal = useCallback(() => setIsViewSchema(true), []);
	const closeSchemaModal = useCallback(() => {
		setIsViewSchema(false);
		setSelectedSchema(null);
	}, []);

	const openDeleteConfirm = useCallback(() => setIsDeleteConfirm(true), []);
	const closeDeleteConfirm = useCallback(() => {
		setIsDeleteConfirm(false);
		setSelectedSchema(null);
	}, []);

	const { exportSchema, isLoadingExportSchema } = useMutateSchemaExport({
		isOranVendor,
	});

	const handleExportConfig = useCallback((id?: string) => {
		if (!id) return;

		exportSchema({ ids: [id] });
	}, [exportSchema]);

	const handleImportYangFiles = useCallback((name?: string) => {
		setImportYangName(name || '');
		setIsImportYangFilesModalOpen(true);
	}, []);

	const handleClearFolder = useCallback((id?: string) => {
		setClearFolderId(id);
		setIsClearFolderConfirmOpen(true);
	}, []);

	const handleRebuild = useCallback((data: IRebuildSchema) => {
		setRebuildFolderData(data);
		setIsRebuildConfirmOpen(true);
	}, []);

	const closeClearConfirm = useCallback(() => {
		setIsClearFolderConfirmOpen(false);
		setClearFolderId(undefined);
	}, []);

	const closeRebuildConfirm = useCallback(() => {
		setIsRebuildConfirmOpen(false);
		setRebuildFolderData(undefined);
	}, []);

	const columns = useMemo(() => getColumns({
		setSelectedSchema,
		openConfigTree: openSchemaModal,
		openDeleteConfirm,
		handleExportConfig,
		isOranVendor,
		isEditAccess,
		handleImportYangFiles,
		handleClearFolder,
		handleRebuild,
	}), [
		setSelectedSchema,
		openSchemaModal,
		openDeleteConfirm,
		handleExportConfig,
		isOranVendor,
		isEditAccess,
		handleImportYangFiles,
		handleClearFolder,
		handleRebuild,
	]);

	const {
		page,
		pageSize,
		sortFieldName,
		sortDirection,
	} = useAppSelector(schemaConfigSelector);

	const {
		schemaConfig,
		countOfRecords,
		isChanged,
		isSet,
		updatedAt,
		isLoadingSchemaConfig,
	} = useQuerySchemaConfig({
		page,
		pageSize,
		sortFieldName,
		sortDirection,
		isOranVendor,
		vendor: selectedVendor?.value,
	});

	const toggleImportForm = useCallback(() => setIsImportFormOpen(prevState => !prevState), []);

	const toggleImportYarnModal = useCallback(() => {
		setIsImportYangFilesModalOpen(prevState => !prevState);
		setIsConfirmYangModalNeeded(false);
	}, []);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => {
		return {
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		};
	}, [sortFieldName, sortDirection, handleSort]);

	const handleExportModals = useCallback((ids: string[], actionType?: TMenuActionsTypes) => {
		return exportSchema({
			ids: ids.filter(Boolean),
			actionType,
		});
	}, [exportSchema]);

	const schemaTreeTitle = useMemo(() => {
		return (
			(isOranVendor ? selectedSchema?.presetName : selectedSchema?.name)
			+ (isOranVendor ? ' - ' + selectedSchema?.moduleName : '')
			+ ' - ' + selectedSchema?.namespace
			+ ' - ' + selectedSchema?.prefix
			+ (isOranVendor ? '' : ' - ' + selectedSchema?.version)
		);
	}, [
		isOranVendor,
		selectedSchema?.moduleName,
		selectedSchema?.name,
		selectedSchema?.namespace,
		selectedSchema?.prefix,
		selectedSchema?.presetName,
		selectedSchema?.version,
	]);

	const { deleteSchemaConfig, isLoadingDeleteSchemaConfig } = useMutateSchemaDelete({
		onSuccess: () => {
			closeDeleteConfirm();
			closeClearConfirm();
		},
		filter: {
			page,
			pageSize,
			sortDirection,
			sortFieldName,
		},
	});

	const { clearSchemaConfig, isLoadingClearSchemaConfig } = useMutateSchemaClear({
		onSuccess: () => {
			closeDeleteConfirm();
			closeClearConfirm();
		},
	});

	const { rebuildSchemaConfig, isLoadingRebuildSchemaConfig } = useMutateSchemaRebuild({
		onSuccess: () => {
			closeRebuildConfirm();
		},
	});

	const { deleteOranSchemaConfig, isLoadingDeleteOranSchemaConfig } = useMutateSchemaDeleteOran({
		onSuccess: () => {
			closeDeleteConfirm();
			closeClearConfirm();
		},
	});

	const handleOranDelete = useCallback(
		(fileIds: string[], folderIds: string[], selectedFolderIds: string[]) => {
			switch (actionId) {
				case EMenuActionsTypes.all:
					return deleteOranSchemaConfig({ isAll: true });
				case EMenuActionsTypes.currentPage:
					return deleteOranSchemaConfig({ folderIds });
				case EMenuActionsTypes.selected:
					return deleteOranSchemaConfig({ fileIds, folderIds: selectedFolderIds });
				default:
					return deleteOranSchemaConfig({ fileIds });
			}
		},
		[actionId, deleteOranSchemaConfig],
	);

	const handleDefaultDelete = useCallback(
		(fileIds: string[]) => {
			if (selectedSchema?.id) {
				return deleteSchemaConfig({
					ids: [selectedSchema.id],
					actionType: EMenuActionsTypes.selected,
				});
			}

			if (actionId) {
				return deleteSchemaConfig({
					ids: fileIds,
					actionType: actionId,
				});
			}
		},
		[actionId, deleteSchemaConfig, selectedSchema?.id],
	);

	const handleDeleteConfig = useCallback(() => {
		const fileIds = selectedSchema?.id && !actionId ? [selectedSchema.id] : checkedIds.filter(Boolean);
		const selectedFolderIds = checkedFolderIds.filter(folder => folder.allChecked).map(folder => folder.folderId);
		const folderIds = checkedFolderIds.map(folder => folder.folderId);

		isOranVendor ? handleOranDelete(fileIds, folderIds, selectedFolderIds) : handleDefaultDelete(fileIds);
	}, [
		actionId,
		checkedFolderIds,
		checkedIds,
		handleOranDelete,
		handleDefaultDelete,
		isOranVendor,
		selectedSchema?.id,
	]);

	const handleClearConfig = useCallback(() => {
		if (clearFolderId) {
			clearSchemaConfig({ clearFolderId });
		}
	}, [clearFolderId, clearSchemaConfig]);

	const handleRebuildConfig = useCallback(() => {
		if (rebuildFolderData) {
			rebuildSchemaConfig(rebuildFolderData);
		}
	}, [rebuildFolderData, rebuildSchemaConfig]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		openDeleteConfirm();
		setCheckedIds(data.ids.filter(Boolean));
		setActionId(data.actionType);
		setCheckedFolderIds(data.folderIds || []);
	}, [openDeleteConfirm]);

	return isFeatureAvailable ? (
		<div className={styles.configurationDataModelManagementContainer}>
			{(isLoadingExportSchema || isLoadingExportSchema) && <SpinnerOverlay />}
			<AttachSchemaModel
				isImportOpen={isImportFormOpen}
				isOranVendor={isOranVendor}
				onClose={toggleImportForm}
			/>
			<Modal
				isLoading={isLoadingDeleteSchemaConfig || isLoadingDeleteOranSchemaConfig}
				isOpen={isDeleteConfirm}
				onClose={closeDeleteConfirm}
			>
				<Confirm
					onSubmit={handleDeleteConfig}
					question='Are you sure you want to delete the Data Model configuration?'
				>
					This action may affect the scheme and links to other Data Model configurations.
				</Confirm>
			</Modal>

			<Modal
				isLoading={isLoadingClearSchemaConfig}
				isOpen={isClearFolderConfirmOpen}
				onClose={closeClearConfirm}
			>
				<Confirm
					onSubmit={handleClearConfig}
					question='Are you sure you want to clear the folder?'
				>
					All Data Model configurations under this folder will be removed.
				</Confirm>
			</Modal>

			<Modal
				isLoading={isLoadingRebuildSchemaConfig}
				isOpen={isRebuildConfirmOpen}
				onClose={closeRebuildConfirm}
			>
				<Confirm
					onSubmit={handleRebuildConfig}
					question='Are you sure you want to rebuild the folder?'
				/>
			</Modal>

			<Modal
				height='80vh'
				isOpen={isViewSchema && !!selectedSchema}
				onClose={closeSchemaModal}
				title={schemaTreeTitle}
				width='700px'
			>
				<SchemaTree
					id={selectedSchema?.id}
					isOranVendor={isOranVendor}
					onClose={closeSchemaModal}
				/>
			</Modal>

			<Modal
				isConfirmDialogNeededOnClose={isConfirmYangModalNeeded}
				isDirtyForm={isConfirmYangModalNeeded}
				isOpen={isImportYangFilesModalOpen && !!importYangName}
				isScrollableContent
				onClose={toggleImportYarnModal}
				title={`Upload YANG files for ${importYangName}`}
				width={MODAL_WIDTH_580}
			>
				<AttachYangFiles
					importYangName={importYangName}
					onClose={toggleImportYarnModal}
					setIsConfirmYangModalNeeded={setIsConfirmYangModalNeeded}
				/>
			</Modal>

			<PageTitle
				icon={Icons.manageSiteDBInverse()}
				title={EGroupMenuTitles.ConfigurationDataModelManagement}
			/>
			<div className={styles.configurationDataModelManagementTableContainer}>
				{!!selectedVendor?.value
					&& <Table
						addIcon={Icons.import}
						addText='Import new'
						columns={columns}
						customExportActions={menuActionsSchema}
						customLeftAction={
							isOranVendor
								? null
								: <ValidationRules
									countOfRecords={countOfRecords}
									isChanged={isChanged}
									isSet={isSet}
									updatedAt={updatedAt}
								/>
						}
						customRemoveActions={menuActionsSchema}
						data={schemaConfig}
						exportTooltipText='Export'
						handleCustomDelete={handleCustomDelete}
						handleExport={handleExportModals}
						isCreatable
						isEditAccess={isEditAccess}
						isExport
						isLoading={isLoadingSchemaConfig}
						isWithoutFilter
						pagination={pagination}
						setCheckedIds={setCheckedIds}
						sort={sort}
						toggleAddModal={toggleImportForm}
					/>
				}
			</div>
		</div>
	) : <UnavailableFeatureMessage vendorName={selectedVendor?.value} />;
};

export default memo(ConfigurationDataModelManagement);
